
import React, { useMemo } from 'react';
import { RichTextViewer } from '@/components/ui/rich-text-viewer/RichTextViewer';
import { PackingListDisplay } from '@/components/packing/PackingListDisplay';
import { InlineAffiliateCTA } from '@/components/affiliate/InlineAffiliateCTA';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AffiliatePartner } from '@/hooks/useAffiliatePartners';

interface BlogPostContentProps {
  content: string;
  blogPostId?: string;
  loading: boolean;
  isNavigating: boolean;
}

export function BlogPostContent({ content, blogPostId, loading, isNavigating }: BlogPostContentProps) {
  // Parse affiliate CTA markers and extract partner IDs
  const partnerIds = useMemo(() => {
    const regex = /<!-- AFFILIATE_CTA:([^:]+):[^>]+ -->/g;
    const ids: string[] = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      ids.push(match[1]);
    }
    return [...new Set(ids)];
  }, [content]);

  // Fetch all needed partners
  const { data: partners } = useQuery({
    queryKey: ['affiliate-partners-for-content', partnerIds],
    queryFn: async () => {
      if (partnerIds.length === 0) return [];
      const { data } = await supabase
        .from('affiliate_partners')
        .select('*')
        .in('id', partnerIds);
      return data as AffiliatePartner[];
    },
    enabled: partnerIds.length > 0,
  });

  // Process content to render affiliate CTAs
  const processedContent = useMemo(() => {
    if (!partners || partners.length === 0) return content;
    
    let processed = content;
    const regex = /<!-- AFFILIATE_CTA:([^:]+):(compact|full)(?::([^>]+))? -->/g;
    
    const parts: Array<{ type: 'text' | 'cta', content: string | React.ReactNode }> = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = regex.exec(content)) !== null) {
      const [fullMatch, partnerId, displayType, context] = match;
      const partner = partners.find(p => p.id === partnerId);
      
      if (partner) {
        // Add text before the marker
        if (match.index > lastIndex) {
          parts.push({ type: 'text', content: content.substring(lastIndex, match.index) });
        }
        
        // Add the CTA component
        parts.push({ 
          type: 'cta', 
          content: (
            <InlineAffiliateCTA
              key={`affiliate-${partnerId}-${key++}`}
              partner={partner}
              context={context}
              compact={displayType === 'compact'}
            />
          )
        });
        
        lastIndex = match.index + fullMatch.length;
      }
    }
    
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push({ type: 'text', content: content.substring(lastIndex) });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content }];
  }, [content, partners]);

  return (
    <>
      <div className="mb-8">
        {Array.isArray(processedContent) ? (
          processedContent.map((part, idx) => 
            part.type === 'text' ? (
              <RichTextViewer 
                key={`text-${idx}`}
                content={part.content as string} 
                loading={loading}
                preserveContentWhileLoading={!!content}
              />
            ) : (
              <React.Fragment key={`cta-${idx}`}>
                {part.content}
              </React.Fragment>
            )
          )
        ) : (
          <RichTextViewer 
            content={processedContent as string} 
            loading={loading}
            preserveContentWhileLoading={!!content}
          />
        )}
      </div>
      
      {blogPostId && (
        <div className={`packing-list ${isNavigating ? 'opacity-70' : ''}`}>
          <PackingListDisplay blogPostId={blogPostId} />
        </div>
      )}
    </>
  );
}
